import React, { useState, useEffect, useContext } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const MetaCapiConfig = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [enabled, setEnabled] = useState(false);
  const [pixelId, setPixelId] = useState("");
  const [token, setToken] = useState("");
  const [hasToken, setHasToken] = useState(false);
  const [trocarToken, setTrocarToken] = useState(false);
  const [queues, setQueues] = useState([]);
  const [qualifyQueueIds, setQualifyQueueIds] = useState([]);
  const [testEventCode, setTestEventCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/meta-capi-settings")
      .then(({ data }) => {
        setEnabled(!!data.enabled);
        setPixelId(data.pixelId || "");
        setHasToken(!!data.hasToken);
        setQualifyQueueIds(
          Array.isArray(data.qualifyQueueIds) ? data.qualifyQueueIds : []
        );
        setTestEventCode(data.testEventCode || "");
      })
      .catch(toastError);

    api
      .get("/queue")
      .then(({ data }) => setQueues(Array.isArray(data) ? data : []))
      .catch(() => setQueues([]));
  }, []);

  const salvar = () => {
    setSaving(true);
    const payload = {
      enabled,
      pixelId,
      qualifyQueueIds,
      testEventCode,
    };
    // Envia o token quando o campo esta editavel e preenchido: primeira vez
    // (ainda sem token) ou quando o admin clicou em Substituir. Sem o
    // "!hasToken", a primeira configuracao nunca gravava o token (bug).
    if (token.trim() && (!hasToken || trocarToken)) payload.accessToken = token.trim();

    api
      .put("/meta-capi-settings", payload)
      .then(() => {
        toast.success("Integração salva.");
        setToken("");
        setTrocarToken(false);
        setHasToken(hasToken || (trocarToken && !!token.trim()));
      })
      .catch(toastError)
      .finally(() => setSaving(false));
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Integração Meta</Title>
      </MainHeader>

      <Box sx={{ p: 2, maxWidth: 720 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Envie ao Meta os leads que chegam pelo WhatsApp para o algoritmo
          aprender quais anúncios trazem gente que fecha. O <strong>Pixel ID</strong>{" "}
          diz para qual conta o evento vai; o <strong>Token</strong> prova que o
          Zappro tem permissão de enviar. Cada empresa usa os seus — assim seus
          dados ficam isolados.
        </Alert>

        <Paper sx={{ p: 2.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!isAdmin}
              />
            }
            label="Ativar envio de eventos ao Meta"
          />

          <TextField
            fullWidth
            size="small"
            label="Pixel ID (Dataset)"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value)}
            disabled={!isAdmin}
            sx={{ mt: 2 }}
          />

          {hasToken && !trocarToken ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Access Token"
                value="•••••••••• configurado"
                disabled
              />
              <Button
                size="small"
                onClick={() => setTrocarToken(true)}
                disabled={!isAdmin}
              >
                Substituir
              </Button>
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Access Token"
              placeholder="Cole aqui o token gerado no Events Manager"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={!isAdmin}
              sx={{ mt: 2 }}
              type="password"
            />
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            O lead e marcado como <strong>qualificado</strong> ao ser movido para a
            coluna <strong>Qualificado</strong> no seu Kanban. Essa etapa e fixa e
            igual para todas as empresas &mdash; nao precisa configurar nada aqui.
          </Alert>

          <TextField
            fullWidth
            size="small"
            label="Test Event Code (opcional)"
            value={testEventCode}
            onChange={(e) => setTestEventCode(e.target.value)}
            disabled={!isAdmin}
            helperText="Use para conferir os eventos em Events Manager → Testar eventos, antes de valer."
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2.5, textAlign: "right" }}>
            <Button
              variant="contained"
              onClick={salvar}
              disabled={!isAdmin || saving}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </Paper>

        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography sx={{ fontWeight: 600 }}>
              Como conseguir o Pixel ID e o Token?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography component="div" sx={{ fontSize: 14 }}>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>
                  Acesse o <strong>Meta Events Manager</strong>{" "}
                  (business.facebook.com/events_manager).
                </li>
                <li>
                  Selecione (ou crie) o <strong>Dataset/Pixel</strong> da sua
                  empresa. O número no topo é o <strong>Pixel ID</strong>.
                </li>
                <li>
                  Abra <strong>Configurações</strong> do dataset → seção{" "}
                  <strong>API de Conversões</strong> →{" "}
                  <strong>Gerar token de acesso</strong>.
                </li>
                <li>
                  Copie a string gerada (começa com <code>EAA...</code>) e cole no
                  campo <strong>Access Token</strong>.
                </li>
                <li>
                  (Opcional) Em <strong>Testar eventos</strong>, copie o{" "}
                  <strong>Test Event Code</strong> para conferir os primeiros
                  envios.
                </li>
                <li>Ligue o interruptor e salve.</li>
              </ol>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 2 }}>
          Privacidade: o telefone do lead é enviado ao Meta de forma cifrada
          (hash), nunca em texto puro. Recomendamos citar o uso do Meta na
          política de privacidade da sua empresa.
        </Typography>
      </Box>
    </MainContainer>
  );
};

export default MetaCapiConfig;
